import { useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import {
  IndexColumn,
  SourceColumnContent,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { getWorkAssignments } from "features/WorkAssignments/services/WorkAssignmentService";
import type { WorkAssignment } from "features/WorkAssignments/types/work-assignment.types";
import {
  WorkAssignmentSourceType,
  WorkAssignmentSourceTypeLabel,
  workAssignmentStatusMap,
  type WorkAssignmentStatus,
} from "features/WorkAssignments/constants/work-assignment.constants";
import { isWorkAssignmentActive } from "features/WorkAssignments/utils/work-assignment.utils";
import moment from "moment";
import WorkAssignmentListFilters from "./WorkAssignmentListFilters";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { RoleCode } from "features/RolesAndPermissions/constants/role.constants";

function formatShortDate(iso?: string | null): string | null {
  if (iso == null || String(iso).trim() === "") return null;
  const m = moment(iso);
  return m.isValid() ? m.format("D MMM YYYY") : null;
}

function plannedWindowLabel(a: WorkAssignment): string | null {
  const s = formatShortDate(a.plannedStartDate);
  const e = formatShortDate(a.plannedEndDate);
  if (s && e) return `${s} → ${e}`;
  if (s) return `${s} → …`;
  if (e) return `… → ${e}`;
  return null;
}

const ScheduleTableView = () => {
  const { user, highestRole } = useProfile();
  const isProjectEngineer = highestRole?.code === RoleCode.PROJECT_ENGINEER;
  const tableRef = useRef<() => void>(null);
  const [selectedStatus, setSelectedStatus] = useState<WorkAssignmentStatus | "">("");
  const [selectedSourceType, setSelectedSourceType] = useState<WorkAssignmentSourceType | "">("");
  const [assigneeUserId, setAssigneeUserId] = useState<number | null>(null);

  const requestParams = useMemo(
    () => ({
      status: selectedStatus || undefined,
      sourceType: selectedSourceType || undefined,
      toUserId: isProjectEngineer ? user?.id : assigneeUserId ?? undefined,
    }),
    [assigneeUserId, selectedSourceType, selectedStatus, isProjectEngineer, user?.id],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (selectedStatus) n += 1;
    if (selectedSourceType) n += 1;
    if (assigneeUserId != null) n += 1;
    return n;
  }, [assigneeUserId, selectedSourceType, selectedStatus]);

  const clearListFilters = useCallback(() => {
    setSelectedStatus("");
    setSelectedSourceType("");
    setAssigneeUserId(null);
  }, []);

  const columns: ColumnDef<WorkAssignment>[] = useMemo(
    () => [
      IndexColumn<WorkAssignment>(),
      {
        header: "Source",
        id: "source",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: import("@tanstack/react-table").Row<WorkAssignment> }) => {
          const a = row.original;
          const cell = resolveMappedSourceCell(a.sourceType, a.sourceId, {
            typeLabelMap: WorkAssignmentSourceTypeLabel,
            source: a.source as { code?: string | null } | null,
          });
          const title = (a.source as any)?.title?.trim() || null;
          return (
            <div className="min-w-0">
              <SourceColumnContent value={cell} />
              {title ? (
                <div
                  className="text-muted small mt-1 text-truncate"
                  style={{ maxWidth: "13rem" }}
                >
                  {title}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        header: "Assignee",
        id: "assignee",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const u = row.original.user;
          const fullName = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
          const label = fullName || u?.email || null;
          const employeeId = u?.employee?.id ?? null;
          if (!u || !label) return <span className="text-muted">—</span>;
          return employeeId != null ? (
            <Link to={`/employees/${employeeId}`} className="link fw-medium text-decoration-none">
              {label}
            </Link>
          ) : (
            <span className="fw-medium">{label}</span>
          );
        },
      },
      {
        header: "Planned dates",
        id: "plannedDates",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const planned = plannedWindowLabel(row.original);
          return planned ? (
            <span className="small">{planned}</span>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Actual start",
        id: "actualStart",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.actualStartDate ? (
            <DateTime date={row.original.actualStartDate} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Actual end",
        id: "actualEnd",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.actualEndDate ? (
            <DateTime date={row.original.actualEndDate} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const a = row.original;
          const s = a.status;
          return s && s in workAssignmentStatusMap ? (
            <StatusBadge value={s} map={workAssignmentStatusMap} />
          ) : isWorkAssignmentActive(a) ? (
            <span className="badge bg-success-subtle text-success">Active</span>
          ) : (
            <span className="badge bg-secondary-subtle text-secondary">Ended</span>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <TableContainer
      columns={columns}
      fetchApi={getWorkAssignments}
      requestParams={requestParams}
      pageSize={10}
      globalSearch
      SearchPlaceholder="Search assignment or linked record…"
      refreshRef={tableRef}
      divClass="table-responsive table-card mb-3"
      tableClass="align-middle table mb-0"
      theadClass="table-light text-muted"
      toolbarRight={
        <div className="d-flex align-items-center gap-2">
          <WorkAssignmentListFilters
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            selectedSourceType={selectedSourceType}
            onSourceTypeChange={setSelectedSourceType}
            assigneeUserId={assigneeUserId}
            onAssigneeUserIdChange={setAssigneeUserId}
          />
        </div>
      }
      filterCount={filtersBadgeCount}
      onFiltersClear={clearListFilters}
    />
  );
};

export default ScheduleTableView;
